import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Sparkles, Trash2 } from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { Switch } from '../../ui/Switch/Switch'
import { useScopedToast } from '../../ui/Toaster/useScopedToast'
import { calendarScheduleSettingsKey } from '../calendar/calendar-queries'
import {
  schedulingPolicyPayloadSchema,
  workingHoursPayloadSchema,
  type SchedulingDayKey,
  type SchedulingPolicyBreak,
  type SchedulingPolicyPayload,
  type SchedulingSpecialDay,
  type WorkingHours,
  type WorkingHoursResponse,
} from './settings-contracts'
import {
  isSettingsPermissionError,
  type SettingsRepository,
} from './settings-repository'
import type { ProviderResourceRepository } from './provider-resource-repository'
import { SettingsSaveFooter } from './SettingsSaveFooter'
import { useReportUnsavedChanges } from './settings-unsaved-changes'
import { TechnicalDetailsPanel } from './TechnicalDetailsPanel'
import panel from './SettingsSection.module.css'
import styles from './WorkingHoursSettings.module.css'
import type { ProviderResourceSettings } from './provider-resource-contracts'

type DayKey = keyof WorkingHours
type DayDraft = { open: boolean; start: string; end: string }
type ScheduleDraft = Record<DayKey, DayDraft>
export type PolicyBaseline = WorkingHoursResponse & {
  policyRevision?: number
  breaks: SchedulingPolicyBreak[]
  specialDays: SchedulingSpecialDay[]
}
type BreakDraft = SchedulingPolicyBreak & { key: string }
type SpecialDayDraft = {
  key: string
  name: string
  date: string
  status: 'OPEN' | 'CLOSED'
  start: string
  end: string
}

type PmsWorkingHours = NonNullable<
  ProviderResourceSettings['providerResources']
>['pmsWorkingHours']

const DAYS: Array<{ key: DayKey; label: string }> = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
]

function validTimeZone(value: string) {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value })
    return Boolean(value)
  } catch {
    return false
  }
}

function pmsScheduleLabel(item: PmsWorkingHours[number]) {
  if (item.specificDate) return item.specificDate
  if (item.days.length > 0) return item.days.join(', ')
  if (item.customRecurrence) {
    return `Every ${item.customRecurrence.num} ${item.customRecurrence.unit} from ${item.customRecurrence.ref}`
  }
  return 'Specific schedule'
}

type PmsScheduleGroup = {
  key: string
  schedule: string
  hours: string
  provider: string | null
  operatories: string[]
  appointmentTypes: string[]
  records: number
}

// NexHealth keeps one record per operatory; the same schedule for the same
// provider and appointment types reads once, with its operatories together.
export function groupPmsWorkingHours(records: PmsWorkingHours) {
  const groups = new Map<string, PmsScheduleGroup>()
  for (const item of records) {
    const schedule = pmsScheduleLabel(item)
    const hours = item.active ? `${item.beginTime}–${item.endTime}` : 'Inactive'
    const key = JSON.stringify([
      schedule,
      hours,
      item.providerDisplayLabel ?? null,
      item.appointmentTypeNames,
    ])
    const group = groups.get(key) ?? {
      key,
      schedule,
      hours,
      provider: item.providerDisplayLabel ?? null,
      operatories: [],
      appointmentTypes: item.appointmentTypeNames,
      records: 0,
    }
    group.records += 1
    if (item.operatoryName && !group.operatories.includes(item.operatoryName))
      group.operatories.push(item.operatoryName)
    groups.set(key, group)
  }
  return [...groups.values()]
}

function PmsWorkingHoursEvidence({ records }: { records: PmsWorkingHours }) {
  const groups = groupPmsWorkingHours(records)
  return (
    <div className={styles.contextBlock}>
      <div className={styles.blockHeader}>
        <div>
          <h3>NexHealth bookable schedule evidence</h3>
          <p>
            Provider, operatory and appointment-type schedules from NexHealth.
            Flio does not write these records back, and they may differ from the
            clinic’s general operating hours.
          </p>
        </div>
        <span className={styles.recordCount}>
          {records.length} {records.length === 1 ? 'record' : 'records'}
        </span>
      </div>
      {groups.length ? (
        <ul className={styles.evidenceList}>
          {groups.map((group) => {
            const resources = [
              group.provider,
              group.operatories.join(', '),
            ].filter(Boolean)
            return (
              <li key={group.key} className={styles.evidenceRow}>
                <div className={styles.evidenceSchedule}>
                  <strong>{group.schedule}</strong>
                  <span>{group.hours}</span>
                </div>
                <div className={styles.evidenceDetail}>
                  {resources.length ? (
                    <p className={styles.evidenceResources}>
                      {resources.join(' · ')}
                    </p>
                  ) : null}
                  {group.appointmentTypes.length ? (
                    <p className={styles.evidenceTypes}>
                      {group.appointmentTypes.join(' · ')}
                    </p>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className={styles.empty}>No NexHealth schedule records.</p>
      )}
    </div>
  )
}

type EffectiveSchedulingHours = NonNullable<
  NonNullable<
    ProviderResourceSettings['providerResources']
  >['effectiveSchedulingHours']
>

function intervalsLabel(intervals: Array<{ start: string; end: string }>) {
  return intervals.length
    ? intervals
        .map((interval) => `${interval.start}–${interval.end}`)
        .join(', ')
    : 'Closed'
}

function EffectiveBookingHours({ hours }: { hours: EffectiveSchedulingHours }) {
  const overrides = Object.entries(hours.dateOverrides)
  return (
    <div className={styles.contextBlock}>
      <div className={styles.blockHeader}>
        <div>
          <h3>Effective Flio booking hours</h3>
          <p>
            Where the hours above and NexHealth’s schedules overlap: the only
            times Flio offers appointments. Local to {hours.timeZone}.
          </p>
        </div>
      </div>
      <dl className={styles.effectiveList}>
        {DAYS.map(({ key, label }) => (
          <div key={key} data-closed={!hours.weekly[key].length || undefined}>
            <dt>{label}</dt>
            <dd>{intervalsLabel(hours.weekly[key])}</dd>
          </div>
        ))}
      </dl>
      {overrides.length ? (
        <div className={styles.effectiveOverrides}>
          <strong>Date-specific changes</strong>
          <ul>
            {overrides.map(([date, intervals]) => (
              <li key={date}>
                {date}: {intervalsLabel(intervals)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

const SHORT_DAYS: Array<{ key: SchedulingDayKey; label: string }> = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
]

function draftFromHours(hours: WorkingHours): ScheduleDraft {
  return Object.fromEntries(
    DAYS.map(({ key }) => {
      const interval = hours[key]
      const closed = interval === '00:00-00:00'
      return [
        key,
        {
          open: !closed,
          start: closed ? '09:00' : interval.slice(0, 5),
          end: closed ? '17:00' : interval.slice(6),
        },
      ]
    })
  ) as ScheduleDraft
}

function payloadFromDraft(
  draft: ScheduleDraft | null,
  breaks: BreakDraft[],
  specialDays: SpecialDayDraft[]
): SchedulingPolicyPayload | null {
  if (!draft) return null
  return {
    workingHours: Object.fromEntries(
      DAYS.map(({ key }) => [
        key,
        draft[key].open
          ? `${draft[key].start}-${draft[key].end}`
          : '00:00-00:00',
      ])
    ) as WorkingHours,
    breaks: policyBreaks(breaks),
    specialDays: policySpecialDays(specialDays),
  }
}

function breakDrafts(
  value: Array<Omit<SchedulingPolicyBreak, 'name'> & { name?: string }>
) {
  return value.map((item, index) => ({
    ...item,
    name: item.name ?? `Break ${index + 1}`,
    key: `break-${index}-${item.days.join('-')}-${item.start}-${item.end}`,
  }))
}

function specialDayDrafts(
  value: Array<
    | { name?: string; date: string; status: 'CLOSED' }
    | {
        name?: string
        date: string
        status: 'OPEN'
        start: string
        end: string
      }
  >
): SpecialDayDraft[] {
  return value.map((item, index) => ({
    ...item,
    name: item.name ?? `Special hours ${index + 1}`,
    start: item.status === 'OPEN' ? item.start : '09:00',
    end: item.status === 'OPEN' ? item.end : '17:00',
    key: `special-${index}-${item.date}`,
  }))
}

function policyBreaks(value: BreakDraft[]): SchedulingPolicyBreak[] {
  return value.map((item) => ({
    name: item.name,
    days: item.days,
    start: item.start,
    end: item.end,
  }))
}

function policySpecialDays(value: SpecialDayDraft[]): SchedulingSpecialDay[] {
  return value.map((item) =>
    item.status === 'CLOSED'
      ? { name: item.name, date: item.date, status: 'CLOSED' }
      : {
          name: item.name,
          date: item.date,
          status: 'OPEN',
          start: item.start,
          end: item.end,
        }
  )
}

function hoursFromPolicy(
  workingHours: Record<
    DayKey,
    { status: 'OPEN' | 'CLOSED'; start?: string; end?: string }
  >
): WorkingHours {
  return Object.fromEntries(
    DAYS.map(({ key }) => {
      const rule = workingHours[key]
      return [
        key,
        rule.status === 'OPEN' && rule.start && rule.end
          ? `${rule.start}-${rule.end}`
          : '00:00-00:00',
      ]
    })
  ) as WorkingHours
}

export function WorkingHoursEditor({
  tenantId,
  tenantLabel,
  repository,
  providerResourceRepository,
  informationalAdapter,
  showSchedulingContext = false,
}: {
  tenantId: string
  tenantLabel: string
  repository?: SettingsRepository
  providerResourceRepository?: ProviderResourceRepository
  /** Owners and Admins only: see `canViewClinicTechnicalDetails`. */
  showSchedulingContext?: boolean
  informationalAdapter?: {
    baseline: PolicyBaseline
    save: (
      tenant: string,
      payload: SchedulingPolicyPayload,
      timeZone: string
    ) => Promise<PolicyBaseline>
  }
}) {
  const activeTenant = useRef(tenantId)
  const queryClient = useQueryClient()
  const notify = useScopedToast()
  activeTenant.current = tenantId
  const providerQuery = useQuery({
    queryKey: ['settings', tenantId, 'provider-resources'],
    queryFn: () => providerResourceRepository!.getResources(tenantId),
    enabled: Boolean(providerResourceRepository),
  })
  const usesLegacyPolicy =
    (!informationalAdapter && !providerResourceRepository) ||
    providerQuery.data?.authorityMode === 'TENANT_SECRET'
  const settingsQuery = useQuery({
    queryKey: ['settings', tenantId, 'working-hours'],
    queryFn: () => repository!.getWorkingHours(tenantId),
    enabled: usesLegacyPolicy,
  })
  const providerResources = providerQuery.data?.providerResources ?? null
  const activePolicy = providerResources?.flioSchedulingPolicy ?? null
  const policyInitialization =
    providerResources?.flioSchedulingPolicyInitialization ?? null
  const usesCanonicalPolicy =
    Boolean(informationalAdapter) ||
    (providerQuery.data?.authorityMode === 'PROVIDER_ACCOUNT_V1' &&
      (activePolicy !== null || policyInitialization !== null) &&
      Boolean(providerResourceRepository?.updateSchedulingPolicy))
  const [timeZone, setTimeZone] = useState('')
  const [baseline, setBaseline] = useState<PolicyBaseline | null>(null)
  const [draft, setDraft] = useState<ScheduleDraft | null>(null)
  const [breaks, setBreaks] = useState<BreakDraft[]>([])
  const [specialDays, setSpecialDays] = useState<SpecialDayDraft[]>([])
  const nextDraftKey = useRef(0)

  useEffect(() => {
    setBaseline(null)
    setDraft(null)
    setBreaks([])
    setSpecialDays([])
  }, [tenantId])

  const informationalBaseline = informationalAdapter?.baseline
  useEffect(() => {
    if (!informationalBaseline) return
    const next = informationalBaseline
    setBaseline(next)
    setTimeZone(next.timeZone)
    setDraft(draftFromHours(next.workingHours))
    setBreaks(breakDrafts(next.breaks))
    setSpecialDays(specialDayDrafts(next.specialDays))
  }, [informationalBaseline])

  useEffect(() => {
    if (!usesCanonicalPolicy || !providerResources) return
    const source = activePolicy ?? policyInitialization
    if (!source) return
    const next = {
      policyRevision:
        activePolicy?.policyRevision ??
        policyInitialization?.expectedPolicyRevision,
      workingHours: hoursFromPolicy(source.workingHours),
      timeZone: source.timeZone,
      breaks: policyBreaks(breakDrafts(source.breaks)),
      specialDays: policySpecialDays(specialDayDrafts(source.specialDays)),
    }
    setBaseline(next)
    setDraft(draftFromHours(next.workingHours))
    setBreaks(breakDrafts(source.breaks))
    setSpecialDays(specialDayDrafts(source.specialDays))
  }, [
    activePolicy,
    policyInitialization,
    providerResources,
    usesCanonicalPolicy,
  ])

  useEffect(() => {
    if (usesCanonicalPolicy) return
    if (!settingsQuery.data) return
    setBaseline({ ...settingsQuery.data, breaks: [], specialDays: [] })
    setDraft(draftFromHours(settingsQuery.data.workingHours))
    setBreaks([])
    setSpecialDays([])
  }, [settingsQuery.data, usesCanonicalPolicy])

  const payload = useMemo(
    () => payloadFromDraft(draft, breaks, specialDays),
    [breaks, draft, specialDays]
  )
  const validation = useMemo(
    () =>
      usesCanonicalPolicy
        ? schedulingPolicyPayloadSchema.safeParse(payload)
        : workingHoursPayloadSchema.safeParse(
            payload ? { workingHours: payload.workingHours } : payload
          ),
    [payload, usesCanonicalPolicy]
  )
  const baselinePayload = useMemo(
    () =>
      baseline
        ? usesCanonicalPolicy
          ? {
              workingHours: baseline.workingHours,
              breaks: baseline.breaks,
              specialDays: baseline.specialDays,
            }
          : { workingHours: baseline.workingHours }
        : null,
    [baseline, usesCanonicalPolicy]
  )
  // Days compared in one order: the same hours stored Sunday-first are not a change.
  const comparable = (value: { workingHours: WorkingHours }) =>
    JSON.stringify({
      ...value,
      workingHours: Object.fromEntries(
        DAYS.map(({ key }) => [key, value.workingHours[key]])
      ),
    })
  const isDirty = Boolean(
    baselinePayload &&
    payload &&
    ((Boolean(informationalAdapter) && timeZone !== baseline?.timeZone) ||
      comparable(baselinePayload) !==
        comparable(
          usesCanonicalPolicy ? payload : { workingHours: payload.workingHours }
        ))
  )
  const invalidDays = useMemo(() => {
    const days = new Set<DayKey>()
    if (validation.success) return days
    validation.error.issues.forEach((issue) => {
      const day = issue.path[1]
      if (typeof day === 'string' && DAYS.some(({ key }) => key === day)) {
        days.add(day as DayKey)
      }
    })
    return days
  }, [validation])

  const saveSchedule = useMutation<
    PolicyBaseline,
    Error,
    { requestTenantId: string; requestPayload: SchedulingPolicyPayload }
  >({
    mutationFn: ({
      requestTenantId,
      requestPayload,
    }: {
      requestTenantId: string
      requestPayload: SchedulingPolicyPayload
    }) =>
      informationalAdapter
        ? informationalAdapter.save(requestTenantId, requestPayload, timeZone)
        : usesCanonicalPolicy &&
            providerResources &&
            baseline?.policyRevision !== undefined
          ? providerResourceRepository!.updateSchedulingPolicy!(
              requestTenantId,
              baseline.policyRevision,
              requestPayload
            ).then((result) => {
              const resources = result.providerResources
              if (!resources?.flioSchedulingPolicy) {
                throw new Error('Policy readback is unavailable')
              }
              const readbackPolicy = resources.flioSchedulingPolicy
              return {
                policyRevision: readbackPolicy.policyRevision,
                workingHours: hoursFromPolicy(readbackPolicy.workingHours),
                timeZone: readbackPolicy.timeZone,
                breaks: policyBreaks(breakDrafts(readbackPolicy.breaks)),
                specialDays: policySpecialDays(
                  specialDayDrafts(readbackPolicy.specialDays)
                ),
              }
            })
          : repository!
              .updateWorkingHours(requestTenantId, {
                workingHours: requestPayload.workingHours,
              })
              .then((result) => ({ ...result, breaks: [], specialDays: [] })),
    onSuccess: (saved, variables) => {
      if (providerResourceRepository) {
        void queryClient.invalidateQueries({
          queryKey: [
            'settings',
            variables.requestTenantId,
            'provider-resources',
          ],
        })
      }
      void queryClient.invalidateQueries({
        queryKey: calendarScheduleSettingsKey(variables.requestTenantId),
      })
      if (activeTenant.current !== variables.requestTenantId) return
      setBaseline(saved)
      setDraft(draftFromHours(saved.workingHours))
      setBreaks(breakDrafts(saved.breaks))
      setSpecialDays(specialDayDrafts(saved.specialDays))
      notify('success', 'Working hours saved')
    },
    onError: (_error, variables) => {
      if (activeTenant.current !== variables.requestTenantId) return
      notify(
        'error',
        'Working hours could not be saved. Your changes are still here.'
      )
    },
  })

  function updateDay(day: DayKey, update: Partial<DayDraft>) {
    if (saveSchedule.isPending) return
    setDraft((current) =>
      current ? { ...current, [day]: { ...current[day], ...update } } : current
    )
  }

  function save() {
    if (!isDirty || !validation.success || !payload || saveSchedule.isPending)
      return
    saveSchedule.mutate({
      requestTenantId: tenantId,
      requestPayload: payload,
    })
  }

  function discard() {
    if (!baseline || saveSchedule.isPending) return
    setTimeZone(baseline.timeZone)
    setDraft(draftFromHours(baseline.workingHours))
    setBreaks(breakDrafts(baseline.breaks))
    setSpecialDays(specialDayDrafts(baseline.specialDays))
  }

  function addBreak() {
    nextDraftKey.current += 1
    setBreaks((current) => [
      ...current,
      {
        key: `new-break-${nextDraftKey.current}`,
        name: '',
        days: ['mon', 'tue', 'wed', 'thu', 'fri'],
        start: '12:30',
        end: '13:30',
      },
    ])
  }

  function addSpecialDay() {
    nextDraftKey.current += 1
    setSpecialDays((current) => [
      ...current,
      {
        key: `new-special-${nextDraftKey.current}`,
        name: '',
        date: '',
        status: 'CLOSED',
        start: '09:00',
        end: '17:00',
      },
    ])
  }

  function updateBreak(key: string, update: Partial<BreakDraft>) {
    if (saveSchedule.isPending) return
    setBreaks((current) =>
      current.map((item) => (item.key === key ? { ...item, ...update } : item))
    )
  }

  function updateSpecialDay(key: string, update: Partial<SpecialDayDraft>) {
    if (saveSchedule.isPending) return
    setSpecialDays((current) =>
      current.map((item) => (item.key === key ? { ...item, ...update } : item))
    )
  }

  useReportUnsavedChanges('hours', isDirty)

  // Rule problems from the policy schema, shown on the rule they belong to.
  const ruleIssues = useMemo(() => {
    const found = {
      breaks: new Map<number, string[]>(),
      specialDays: new Map<number, string[]>(),
    }
    if (validation.success) return found
    validation.error.issues.forEach((issue) => {
      const [kind, index] = issue.path
      if (kind !== 'breaks' && kind !== 'specialDays') return
      if (typeof index !== 'number') return
      const list = found[kind].get(index) ?? []
      if (!list.includes(issue.message)) list.push(issue.message)
      found[kind].set(index, list)
    })
    return found
  }, [validation])
  const clinicToday = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: baseline?.timeZone || undefined,
      }).format(new Date())
    } catch {
      return null
    }
  }, [baseline?.timeZone])
  const problem = validation.success
    ? informationalAdapter && !validTimeZone(timeZone)
      ? 'enter a valid timezone to save'
      : null
    : invalidDays.size
      ? 'fix the highlighted day to save'
      : 'fix the highlighted rule to save'

  return (
    <section className={panel.section} aria-labelledby="working-hours-title">
      {providerResourceRepository && providerQuery.isPending ? (
        <Spinner label="Loading scheduling authorities" />
      ) : null}
      {usesLegacyPolicy && settingsQuery.isPending ? (
        <Spinner label="Loading working hours" />
      ) : null}
      {providerResourceRepository && providerQuery.isError ? (
        <StatePanel
          kind="error"
          title="Scheduling authorities unavailable"
          description="Flio policy and PMS hours could not be loaded safely. No legacy hours will be changed."
        />
      ) : null}
      {providerQuery.data?.authorityMode === 'PROVIDER_ACCOUNT_V1' &&
      providerQuery.data.providerResources === null ? (
        <StatePanel
          kind="error"
          title="Scheduling policy unavailable"
          description="The active Flio scheduling policy and current PMS working hours are not ready yet."
        />
      ) : null}
      {usesLegacyPolicy && settingsQuery.isError ? (
        <StatePanel
          kind={
            isSettingsPermissionError(settingsQuery.error)
              ? 'forbidden'
              : 'error'
          }
          title="Working hours unavailable"
          description={
            isSettingsPermissionError(settingsQuery.error)
              ? 'You do not have permission to manage these working hours.'
              : 'The stored schedule could not be loaded safely.'
          }
        />
      ) : null}

      {baseline && draft ? (
        <>
          <div className={panel.panel}>
            <header className={panel.panelHeader}>
              <div className={panel.heading}>
                <span className={panel.eyebrow}>Managed in Flio</span>
                <h2 id="working-hours-title">Working hours</h2>
                <p>
                  When <strong>{tenantLabel}</strong> is open. Flio uses these
                  hours to answer patients, transfer callers who ask for the
                  front desk and offer appointments.
                </p>
              </div>
            </header>

            {policyInitialization ? (
              <div className={panel.notice} data-tone="info">
                <Sparkles size={16} aria-hidden="true" />
                <div>
                  <strong>Create this clinic’s Flio scheduling policy</strong>
                  <p>
                    Set this clinic’s hours and save when ready. Booking stays
                    off until its provider and service setup is active.
                  </p>
                </div>
              </div>
            ) : null}

            <div className={styles.block}>
              <div className={styles.blockHeader}>
                <div>
                  <h3>Regular weekly hours</h3>
                  <p>Times are local to {baseline.timeZone}.</p>
                </div>
                {informationalAdapter ? (
                  <label className={`${panel.field} ${styles.timeZoneField}`}>
                    <span>Timezone</span>
                    <input
                      value={timeZone}
                      disabled={saveSchedule.isPending}
                      aria-invalid={!validTimeZone(timeZone)}
                      onChange={(event) => {
                        setTimeZone(event.target.value)
                      }}
                    />
                  </label>
                ) : null}
              </div>
              <div className={styles.days}>
                <div className={styles.daysHeader} aria-hidden="true">
                  <span>Day</span>
                  <span>Status</span>
                  <span className={styles.timesHeader}>
                    <span>Opens</span>
                    <span>Closes</span>
                  </span>
                </div>
                {DAYS.map(({ key, label }) => {
                  const invalid = invalidDays.has(key)
                  const errorId = `working-hours-${key}-error`
                  const open = draft[key].open
                  return (
                    <fieldset
                      className={styles.dayRow}
                      data-closed={!open || undefined}
                      data-invalid={invalid || undefined}
                      disabled={saveSchedule.isPending}
                      key={key}
                    >
                      <legend className={styles.dayName}>{label}</legend>
                      <label className={styles.openToggle}>
                        <Switch
                          checked={open}
                          aria-label={`${label} open`}
                          onChange={(event) =>
                            updateDay(key, {
                              open: event.currentTarget.checked,
                            })
                          }
                        />
                        <span aria-hidden="true">
                          {open ? 'Open' : 'Closed'}
                        </span>
                      </label>
                      {open ? (
                        <div className={styles.dayTimes}>
                          <label className={styles.timeField}>
                            <span className={panel.srOnly}>{label} opens</span>
                            <input
                              type="time"
                              value={draft[key].start}
                              aria-invalid={invalid}
                              aria-describedby={invalid ? errorId : undefined}
                              onChange={(event) =>
                                updateDay(key, {
                                  start: event.currentTarget.value,
                                })
                              }
                            />
                          </label>
                          <span className={styles.dash} aria-hidden="true">
                            –
                          </span>
                          <label className={styles.timeField}>
                            <span className={panel.srOnly}>{label} closes</span>
                            <input
                              type="time"
                              value={draft[key].end}
                              aria-invalid={invalid}
                              aria-describedby={invalid ? errorId : undefined}
                              onChange={(event) =>
                                updateDay(key, {
                                  end: event.currentTarget.value,
                                })
                              }
                            />
                          </label>
                        </div>
                      ) : (
                        <span className={styles.closedNote}>
                          Closed all day
                        </span>
                      )}
                      {invalid ? (
                        <small className={panel.fieldError} id={errorId}>
                          Closing time must be later than opening time.
                        </small>
                      ) : null}
                    </fieldset>
                  )
                })}
              </div>
            </div>

            {usesCanonicalPolicy ? (
              <div className={styles.block}>
                <div className={styles.blockHeader}>
                  <div>
                    <h3>Business hour breaks</h3>
                    <p>
                      {informationalAdapter
                        ? 'Recurring breaks in the clinic’s business hours.'
                        : 'Recurring times when Flio must not offer appointments, such as lunch.'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={saveSchedule.isPending}
                    onClick={addBreak}
                  >
                    <Plus size={16} aria-hidden="true" /> Add break
                  </Button>
                </div>
                {breaks.length ? (
                  <ul className={styles.rules}>
                    {breaks.map((item, index) => {
                      const issues = ruleIssues.breaks.get(index)
                      const issueId = `break-${item.key}-issues`
                      return (
                        <li key={item.key}>
                          <fieldset
                            className={styles.rule}
                            data-invalid={issues ? true : undefined}
                            disabled={saveSchedule.isPending}
                            aria-describedby={issues ? issueId : undefined}
                          >
                            <legend className={panel.srOnly}>
                              {item.name || `Break ${index + 1}`}
                            </legend>
                            <label
                              className={`${panel.field} ${styles.ruleName}`}
                            >
                              <span>Name</span>
                              <input
                                value={item.name}
                                placeholder="e.g. Lunch"
                                onChange={(event) =>
                                  updateBreak(item.key, {
                                    name: event.currentTarget.value,
                                  })
                                }
                              />
                            </label>
                            <label
                              className={`${panel.field} ${styles.timeField}`}
                            >
                              <span>Starts</span>
                              <input
                                type="time"
                                value={item.start}
                                onChange={(event) =>
                                  updateBreak(item.key, {
                                    start: event.currentTarget.value,
                                  })
                                }
                              />
                            </label>
                            <label
                              className={`${panel.field} ${styles.timeField}`}
                            >
                              <span>Ends</span>
                              <input
                                type="time"
                                value={item.end}
                                onChange={(event) =>
                                  updateBreak(item.key, {
                                    end: event.currentTarget.value,
                                  })
                                }
                              />
                            </label>
                            <fieldset className={styles.dayPicker}>
                              <legend>Applies on</legend>
                              <div>
                                {SHORT_DAYS.map(({ key, label }) => (
                                  <label key={key} className={styles.dayChip}>
                                    <input
                                      type="checkbox"
                                      checked={item.days.includes(key)}
                                      onChange={(event) =>
                                        updateBreak(item.key, {
                                          days: event.currentTarget.checked
                                            ? [...item.days, key]
                                            : item.days.filter(
                                                (day) => day !== key
                                              ),
                                        })
                                      }
                                    />
                                    <span>{label}</span>
                                  </label>
                                ))}
                              </div>
                            </fieldset>
                            <Button
                              type="button"
                              variant="ghost"
                              className={styles.deleteRule}
                              aria-label={`Delete ${item.name || 'break'}`}
                              title="Delete"
                              onClick={() => {
                                setBreaks((current) =>
                                  current.filter(
                                    (candidate) => candidate.key !== item.key
                                  )
                                )
                              }}
                            >
                              <Trash2 size={16} aria-hidden="true" />
                            </Button>
                            {issues ? (
                              <p
                                className={`${panel.fieldError} ${styles.ruleIssues}`}
                                id={issueId}
                              >
                                {issues.join(' ')}
                              </p>
                            ) : null}
                          </fieldset>
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p className={styles.empty}>
                    No recurring breaks configured.
                  </p>
                )}
              </div>
            ) : null}

            {usesCanonicalPolicy ? (
              <div className={styles.block}>
                <div className={styles.blockHeader}>
                  <div>
                    <h3>Holidays and special hours</h3>
                    <p>
                      Date-specific hours replace the regular weekly hours for
                      that day.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={saveSchedule.isPending}
                    onClick={addSpecialDay}
                  >
                    <Plus size={16} aria-hidden="true" /> Add special hours
                  </Button>
                </div>
                {specialDays.length ? (
                  <ul className={styles.rules}>
                    {specialDays.map((item, index) => {
                      const issues = ruleIssues.specialDays.get(index)
                      const issueId = `special-${item.key}-issues`
                      const past = Boolean(
                        clinicToday && item.date && item.date < clinicToday
                      )
                      return (
                        <li key={item.key}>
                          <fieldset
                            className={styles.rule}
                            data-invalid={issues ? true : undefined}
                            disabled={saveSchedule.isPending}
                            aria-describedby={issues ? issueId : undefined}
                          >
                            <legend className={panel.srOnly}>
                              {item.name || `Special hours ${index + 1}`}
                            </legend>
                            <label
                              className={`${panel.field} ${styles.ruleName}`}
                            >
                              <span>Name</span>
                              <input
                                value={item.name}
                                placeholder="e.g. Thanksgiving"
                                onChange={(event) =>
                                  updateSpecialDay(item.key, {
                                    name: event.currentTarget.value,
                                  })
                                }
                              />
                            </label>
                            <label
                              className={`${panel.field} ${styles.dateField}`}
                            >
                              <span>Date</span>
                              <input
                                type="date"
                                value={item.date}
                                onChange={(event) =>
                                  updateSpecialDay(item.key, {
                                    date: event.currentTarget.value,
                                  })
                                }
                              />
                            </label>
                            <label
                              className={`${panel.field} ${styles.scheduleField}`}
                            >
                              <span>Schedule</span>
                              <select
                                value={item.status}
                                onChange={(event) =>
                                  updateSpecialDay(item.key, {
                                    status: event.currentTarget.value as
                                      'OPEN' | 'CLOSED',
                                  })
                                }
                              >
                                <option value="CLOSED">Closed all day</option>
                                <option value="OPEN">Custom hours</option>
                              </select>
                            </label>
                            {item.status === 'OPEN' ? (
                              <>
                                <label
                                  className={`${panel.field} ${styles.timeField}`}
                                >
                                  <span>Opens</span>
                                  <input
                                    type="time"
                                    value={item.start}
                                    onChange={(event) =>
                                      updateSpecialDay(item.key, {
                                        start: event.currentTarget.value,
                                      })
                                    }
                                  />
                                </label>
                                <label
                                  className={`${panel.field} ${styles.timeField}`}
                                >
                                  <span>Closes</span>
                                  <input
                                    type="time"
                                    value={item.end}
                                    onChange={(event) =>
                                      updateSpecialDay(item.key, {
                                        end: event.currentTarget.value,
                                      })
                                    }
                                  />
                                </label>
                              </>
                            ) : null}
                            {past ? (
                              <span className={styles.pastChip}>Past date</span>
                            ) : null}
                            <Button
                              type="button"
                              variant="ghost"
                              className={styles.deleteRule}
                              aria-label={`Delete ${item.name || 'special hours'}`}
                              title="Delete"
                              onClick={() => {
                                setSpecialDays((current) =>
                                  current.filter(
                                    (candidate) => candidate.key !== item.key
                                  )
                                )
                              }}
                            >
                              <Trash2 size={16} aria-hidden="true" />
                            </Button>
                            {issues ? (
                              <p
                                className={`${panel.fieldError} ${styles.ruleIssues}`}
                                id={issueId}
                              >
                                {issues.join(' ')}
                              </p>
                            ) : null}
                          </fieldset>
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p className={styles.empty}>
                    No holidays or special hours configured.
                  </p>
                )}
              </div>
            ) : null}

            <SettingsSaveFooter
              dirty={isDirty}
              saving={saveSchedule.isPending}
              saved={saveSchedule.isSuccess && !isDirty}
              error={
                saveSchedule.isError && isDirty
                  ? 'Not saved. Your changes are still here; try again.'
                  : null
              }
              problem={problem}
              canSave={
                validation.success &&
                (!informationalAdapter || validTimeZone(timeZone))
              }
              onDiscard={discard}
              onSave={save}
            />
          </div>

          {showSchedulingContext && usesCanonicalPolicy && providerResources ? (
            <TechnicalDetailsPanel
              eyebrow="NexHealth scheduling context"
              title="Where Flio can book"
              description="How the provider schedules in NexHealth narrow the hours above."
            >
              {providerResources.effectiveSchedulingHours ? (
                <EffectiveBookingHours
                  hours={providerResources.effectiveSchedulingHours}
                />
              ) : null}
              <PmsWorkingHoursEvidence
                records={providerResources.pmsWorkingHours}
              />
            </TechnicalDetailsPanel>
          ) : null}
        </>
      ) : null}
    </section>
  )
}
